import { useMemo, useRef } from "react";
import { computeEstimateTotals } from './calculations';

export const useEstimateCalculations = ({
  // Product data
  allProducts,
  
  // Sales data
  oldSalesData,
  oldTableData,
  schemeSalesData,
  schemeTableData,
  saleReturnData,
  
  // Discount
  discount = 0,
  
  // Manual net payable amount handling
  manualNetPayAmount = 0,
  manualChangeSeq = 0,
  isManualNetMode = false,
  // Whether discount has been manually computed by user (prevents auto-changes)
  hasManualDiscountCalculation = false,
  suppressReverseOnce = false,
  
  // Tax rate (assuming 3% GST)
  taxRate = 3,
}) => {
  
  // Store the calculated discount when manual mode is active
  const storedDiscountRef = useRef(null);
  // Store the last manual net payable amount to detect changes
  const lastManualNetPayAmountRef = useRef(null);
  
  // Latch discount amount so SR/URD toggles do not change it unless products/discount change
  const latchedDiscountAmtRef = useRef(0);
  const latchedTaxableBaseRef = useRef(null);
  const prevSignatureRef = useRef({ productsSig: '', discount: 0, urdSig: '', schemeSig: '' });
  const prevDeductionsRef = useRef({ oldItemsAmount: 0, salesAmountToPass: 0 });
  const ignoreNextManualChangeRef = useRef(false);

  const buildProductsSignature = (products = []) => {
    try {
      return JSON.stringify(
        products.map(p => ({
          id: p.id || p.code || p.product_name || '',
          pt: parseFloat(p.piece_taxable_amt) || 0,
          d: parseFloat(p.disscount) || 0,
          fd: parseFloat(p.festival_discount) || 0,
        }))
      );
    } catch {
      return String(products.length);
    }
  };

  const buildArrayAmountSignature = (arr = [], key = 'total_amount') => {
    try {
      const len = Array.isArray(arr) ? arr.length : 0;
      const sum = (Array.isArray(arr) ? arr : []).reduce((acc, it) => acc + (parseFloat(it[key]) || 0), 0);
      return `${len}|${sum.toFixed(2)}`;
    } catch {
      return '0|0.00';
    }
  };

  const calculations = useMemo(() => {
    // First compute base totals (non-manual mode) using centralized function
    const base = computeEstimateTotals({
      allProducts,
      oldSalesData,
      oldTableData,
      schemeTableData,
      saleReturnData,
      discount,
      taxRate,
    });

    let { totalAmount } = base;
    const deductionsChanged = (
      Math.abs((prevDeductionsRef.current.oldItemsAmount || 0) - (base.oldItemsAmount || 0)) > 0.009 ||
      Math.abs((prevDeductionsRef.current.salesAmountToPass || 0) - (base.salesAmountToPass || 0)) > 0.009
    );
    const manualChangeDueToDeductions = deductionsChanged && (lastManualNetPayAmountRef.current !== manualNetPayAmount);

    let discountAmt, taxableAmount, taxAmount, netAmount, netPayableAmount, effectiveDiscountPercentage;

    if (isManualNetMode && typeof manualNetPayAmount === 'number') {
      // Check if the manual net payable amount has changed (user edited it)
      const hasManualAmountChanged = lastManualNetPayAmountRef.current !== manualNetPayAmount;

      const desiredNetPayableInteger = Math.round(manualNetPayAmount || 0);
      const totalDeductions = base.oldItemsAmount + base.salesAmountToPass;
      const festivalDiscount = base.festivalDiscountAmt || 0;

      // Helper to compute the final integer net payable for a given discount amount
      // Note: Festival discount is already applied at product level, so we work with the amount after festival discount
      const computeRoundedNetPayableFromDiscount = (proposedDiscountAmount) => {
        const boundedDiscount = Math.max(0, Math.min(proposedDiscountAmount, totalAmount - festivalDiscount));
        const candidateTaxable = totalAmount - festivalDiscount - boundedDiscount;
        const candidateTax = (candidateTaxable * taxRate) / 100;
        const candidateNet = candidateTaxable + candidateTax;
        // Round to 2 decimal places to match the display format
        const candidateNetRounded2 = Math.round(candidateNet * 100) / 100;
        return Math.round(candidateNetRounded2 - totalDeductions);
      };

      // Only run reverse-calculation if user actually edited manual amount and deductions didn't change,
      // and we already have a prior discount stored. Otherwise, initialize with base discount.
      if (
        // On an explicit manual change, perform reverse calc immediately
        (hasManualAmountChanged && !deductionsChanged) &&
        !manualChangeDueToDeductions &&
        !ignoreNextManualChangeRef.current &&
        !suppressReverseOnce &&
        manualChangeSeq > 0 // only reverse when user explicitly changed the amount
      ) {

        

        
        // First-pass discount from algebra (ignoring 2-dec rounding effects)
        // Festival discount is already applied at product level, so we work with the amount after festival discount
        const targetGrossNet = desiredNetPayableInteger + totalDeductions; // desired net before deductions
        const targetTaxableUnrounded = targetGrossNet / (1 + (taxRate / 100));
        let proposedDiscount = (totalAmount - festivalDiscount) - targetTaxableUnrounded;
        proposedDiscount = Math.max(0, Math.min(proposedDiscount, totalAmount - festivalDiscount));

        // Refine discount to hit the integer target after rounding steps
        const taxRateFactor = 1 + (taxRate / 100);
        for (let i = 0; i < 5; i += 1) {
          const currentRounded = computeRoundedNetPayableFromDiscount(proposedDiscount);
          const diff = currentRounded - desiredNetPayableInteger;
          if (diff === 0) break;
          // Adjust discount in the opposite direction of diff
          proposedDiscount = proposedDiscount + (diff / taxRateFactor);
          // Clamp within valid range (considering festival discount is already applied)
          proposedDiscount = Math.max(0, Math.min(proposedDiscount, totalAmount - festivalDiscount));
        }

        // Local neighborhood search around the refined value to account for rounding edges
        // Search locally around proposedDiscount with fine steps to hit exact integer
        let bestDiscount = proposedDiscount;
        let bestDelta = Number.POSITIVE_INFINITY;
        const pushCandidate = (cand) => {
          const rounded = computeRoundedNetPayableFromDiscount(cand);
          const delta = Math.abs(rounded - desiredNetPayableInteger);
          if (delta < bestDelta) {
            bestDelta = delta;
            bestDiscount = cand;
          }
          return delta === 0;
        };

        // Coarse neighbors - try more values around the proposed discount
        const coarseNeighbors = [
          0, -10, -8, -6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6, 8, 10,
        ];
        for (const step of coarseNeighbors) {
          if (pushCandidate(proposedDiscount + step)) break;
        }
        
        // Fine-grained sweep within +/- 10 with 0.01 precision if still not exact
        if (bestDelta !== 0) {
          for (let offset = -10; offset <= 10; offset += 0.01) {
            const cand = proposedDiscount + offset;
            if (pushCandidate(cand)) break;
          }
        }
        
        // If still not exact, try even finer precision
        if (bestDelta !== 0) {
          for (let offset = -2; offset <= 2; offset += 0.001) {
            const cand = proposedDiscount + offset;
            if (pushCandidate(cand)) break;
          }
        }

        discountAmt = Math.max(0, Math.min(bestDiscount, totalAmount - festivalDiscount));
        const finalNetPayable = computeRoundedNetPayableFromDiscount(discountAmt);
        

        
        // If we couldn't find an exact match, adjust the manual net payable amount
        if (finalNetPayable !== desiredNetPayableInteger) {

          // Update the manual net payable amount to match what we can achieve
          lastManualNetPayAmountRef.current = finalNetPayable;
        }
        // Store for stability across unrelated changes
        storedDiscountRef.current = discountAmt;
        lastManualNetPayAmountRef.current = manualNetPayAmount;
        try { console.log(`[calc] manual:reverse discount=${discountAmt.toFixed(2)} total=${totalAmount.toFixed(2)} sr=${(base.salesAmountToPass||0).toFixed(2)} urd=${(base.oldItemsAmount||0).toFixed(2)}`); } catch {}
      } else {
        // Initialize or preserve from base when entering manual mode due to SR/URD
        const seedDiscount = storedDiscountRef.current !== null
          ? storedDiscountRef.current
          : (latchedDiscountAmtRef.current || base.discountAmt);
        discountAmt = seedDiscount;
        storedDiscountRef.current = discountAmt;
        // Keep manual amount ref in sync to avoid re-entering this branch repeatedly
        lastManualNetPayAmountRef.current = manualNetPayAmount;
        try { console.log(`[calc] manual:preserve discount=${discountAmt.toFixed(2)} total=${totalAmount.toFixed(2)} sr=${(base.salesAmountToPass||0).toFixed(2)} urd=${(base.oldItemsAmount||0).toFixed(2)}`); } catch {}
      }

      // Calculate based on the settled discount amount (both regular and festival discount)
      taxableAmount = totalAmount - discountAmt - festivalDiscount;
      taxAmount = (taxableAmount * taxRate) / 100;
      netAmount = taxableAmount + taxAmount;

      // Net payable = net amount - URD - sales return (can be negative)
      // Use the same rounding logic as the search algorithm
      const netAmountRounded = Math.round(netAmount * 100) / 100;
      netPayableAmount = netAmountRounded - base.oldItemsAmount - base.salesAmountToPass;
      
      // In manual mode, return the calculated net payable. Parent handles manual override UI.
      


      // Effective percent for UI sync
      effectiveDiscountPercentage = totalAmount > 0 ? (discountAmt / totalAmount) * 100 : 0;
    } else {
      // Use base totals when not in manual mode
      // Determine if only SR/URD changed; if so, latch the previous discount amount
      const currentProductsSig = buildProductsSignature(allProducts);
      const currentUrdSig = buildArrayAmountSignature(oldSalesData && oldSalesData.length > 0 ? oldSalesData : oldTableData, 'total_amount');
      const currentSchemeSig = buildArrayAmountSignature(schemeTableData, 'total_amount');
      const prev = prevSignatureRef.current;

      const shouldRecalcDiscount = (
        prev.productsSig !== currentProductsSig ||
        prev.discount !== parseFloat(discount || 0)
      );

      // Debug: Log signature changes
      if (prev.productsSig !== currentProductsSig) {
        try { 
          console.log(`[calc] Products signature changed:`, {
            prev: prev.productsSig,
            current: currentProductsSig,
            prevParsed: JSON.parse(prev.productsSig || '[]'),
            currentParsed: JSON.parse(currentProductsSig || '[]')
          }); 
        } catch (e) {
          console.log(`[calc] Products signature changed (parse error):`, {
            prev: prev.productsSig,
            current: currentProductsSig
          });
        }
      }

      if (shouldRecalcDiscount || latchedDiscountAmtRef.current === 0) {
        // Core discount inputs changed (products/percent) or first run → accept recomputed amount
        // However, if we're loading an existing estimate and products have individual discounts, preserve them
        const totalProductDiscount = allProducts.reduce((sum, product) => sum + (parseFloat(product.disscount || 0)), 0);
        
        if (totalProductDiscount > 0 && latchedDiscountAmtRef.current === 0) {
          // This is likely loading an existing estimate with saved product discounts
          // Preserve the total product discount instead of recalculating
          discountAmt = totalProductDiscount;
          try { console.log(`[calc] auto:preserve existing discount=${discountAmt.toFixed(2)} total=${base.totalAmount.toFixed(2)} sr=${(base.salesAmountToPass||0).toFixed(2)} urd=${(base.oldItemsAmount||0).toFixed(2)}`); } catch {}
        } else {
          // Normal recalculation for new estimates or when discount percentage changes
          discountAmt = base.discountAmt;
          try { console.log(`[calc] auto:recalc discount=${discountAmt.toFixed(2)} total=${base.totalAmount.toFixed(2)} sr=${(base.salesAmountToPass||0).toFixed(2)} urd=${(base.oldItemsAmount||0).toFixed(2)}`); } catch {}
        }
        latchedDiscountAmtRef.current = discountAmt;
        latchedTaxableBaseRef.current = base.totalAmount;
      } else {
        // Only deductions (URD/SR/Schemes) changed → keep latched discount
        discountAmt = typeof latchedDiscountAmtRef.current === 'number' ? latchedDiscountAmtRef.current : base.discountAmt;
        try { console.log(`[calc] auto:latch discount=${discountAmt.toFixed(2)} total=${base.totalAmount.toFixed(2)} sr=${(base.salesAmountToPass||0).toFixed(2)} urd=${(base.oldItemsAmount||0).toFixed(2)}`); } catch {}
      }

      // Always update signature snapshot for next comparison
      prevSignatureRef.current = {
        productsSig: currentProductsSig,
        discount: parseFloat(discount || 0),
        urdSig: currentUrdSig,
        schemeSig: currentSchemeSig,
      };
      taxableAmount = base.taxableAmount;
      taxAmount = base.taxAmount;
      netAmount = base.netAmount;
      netPayableAmount = base.netPayableAmount;
      // If user has manually calculated discount, preserve entered percentage
      const percentBase = latchedTaxableBaseRef.current || totalAmount;
      effectiveDiscountPercentage = hasManualDiscountCalculation
        ? (percentBase > 0 ? (discountAmt / percentBase) * 100 : 0)
        : parseFloat(discount || 0);



      // Reset stored values when not in manual mode
      storedDiscountRef.current = null;
      lastManualNetPayAmountRef.current = null;
    }

    const result = {
      totalAmount: base.totalAmount,
      totalMakingCharges: base.totalMakingCharges,
      taxableAmount: parseFloat(taxableAmount.toFixed(2)),
      discountAmt: parseFloat(discountAmt.toFixed(2)),
      festivalDiscountAmt: base.festivalDiscountAmt,
      taxAmount: parseFloat(taxAmount.toFixed(2)),
      netAmount: parseFloat(netAmount.toFixed(2)),
      oldItemsAmount: base.oldItemsAmount,
      schemeAmount: base.schemeAmount,
      salesAmountToPass: base.salesAmountToPass,
      // Always return the calculated net payable amount
      // The parent component will handle manual mode display logic
      netPayableAmount: Math.round(netPayableAmount),
      effectiveDiscountPercentage: parseFloat(effectiveDiscountPercentage.toFixed(2)),
    };

    // Update deduction refs for next run
    prevDeductionsRef.current = {
      oldItemsAmount: base.oldItemsAmount,
      salesAmountToPass: base.salesAmountToPass,
    };

    // If we detected a manual change right after deductions changed, ignore one reverse cycle
    if (manualChangeDueToDeductions) {
      ignoreNextManualChangeRef.current = true;
    } else {
      ignoreNextManualChangeRef.current = false;
    }

    try { console.log(`[calc] summary discount=${result.discountAmt.toFixed(2)} eff%=${result.effectiveDiscountPercentage.toFixed(2)} net=${result.netPayableAmount} total=${result.totalAmount.toFixed(2)} sr=${result.salesAmountToPass.toFixed(2)} urd=${result.oldItemsAmount.toFixed(2)}`); } catch {}
    return result;
  }, [
    allProducts,
    oldSalesData,
    oldTableData,
    schemeTableData,
    saleReturnData,
    discount,
    manualNetPayAmount,
    manualChangeSeq,
    isManualNetMode,
    taxRate,
    hasManualDiscountCalculation,
    suppressReverseOnce,
  ]);

  return calculations;
}; 